import type { HomeDraft, SiteDraft, ThemeDraft } from './api';
import { themeToInlineStyle } from './theme';

export interface PreviewAssets {
  readonly siteOrigin: string;
}

/**
 * Renders a high-fidelity draft overlay that mirrors the live marketing
 * homepage structure (header, hero, sections, footer) from session JSON —
 * without an Astro rebuild. Theme tokens override `.site-preview` CSS vars.
 */
export function renderDraftPreview(
  root: HTMLElement,
  home: HomeDraft | null,
  site: SiteDraft | null,
  theme: ThemeDraft | null,
  assets: PreviewAssets,
): void {
  root.replaceChildren();
  const shell = document.createElement('div');
  shell.className = 'site-preview';
  const themeStyle = themeToInlineStyle(theme);
  if (themeStyle.length > 0) {
    shell.setAttribute('style', themeStyle);
  }

  if (home === null) {
    const empty = document.createElement('p');
    empty.className = 'preview-empty';
    empty.textContent = 'No draft yet.';
    shell.append(empty);
    root.append(shell);
    return;
  }

  shell.append(renderHeader(site, assets.siteOrigin));

  const main = document.createElement('main');
  if (home.hero !== undefined) {
    main.append(renderHero(home.hero));
  }
  if (home.apps !== undefined) {
    main.append(renderApps(home.apps));
  }
  if (home.problem !== undefined) {
    main.append(renderSimpleSection(home.problem));
  }
  if (home.howItWorks !== undefined) {
    main.append(renderHowItWorks(home.howItWorks));
  }
  if (home.roadmap !== undefined) {
    main.append(renderSimpleSection(home.roadmap));
  }
  if (home.credibility !== undefined) {
    main.append(renderCredibility(home.credibility));
  }
  if (home.act !== undefined) {
    main.append(renderAct(home.act));
  }
  shell.append(main);
  shell.append(renderFooter(site));
  root.append(shell);
}

function renderHeader(site: SiteDraft | null, siteOrigin: string): HTMLElement {
  const header = document.createElement('header');
  header.className = 'site-header';
  const inner = document.createElement('div');
  inner.className = 'container site-header__inner';

  const brand = document.createElement('a');
  brand.className = 'brand';
  brand.href = '#';
  brand.setAttribute('aria-label', `${site?.name ?? 'Comita Health'} — home`);

  const mark = document.createElement('img');
  mark.className = 'brand__mark';
  mark.src = `${siteOrigin.replace(/\/$/, '')}/logo-mark.png`;
  mark.alt = '';
  mark.width = 36;
  mark.height = 36;
  brand.append(mark);

  const lockup = document.createElement('span');
  lockup.className = 'brand__lockup';
  const comita = document.createElement('span');
  comita.className = 'brand__comita';
  comita.setAttribute('aria-hidden', 'true');
  comita.textContent = 'comita';
  const line = document.createElement('span');
  line.className = 'brand__line';
  line.textContent = 'Health';
  lockup.append(comita, line);
  brand.append(lockup);
  inner.append(brand);

  const nav = document.createElement('nav');
  nav.className = 'site-nav';
  nav.setAttribute('aria-label', 'Primary');
  const links = document.createElement('div');
  links.className = 'site-nav__links';
  const items = site?.nav ?? [];
  for (const item of items) {
    if (item.cta === true) {
      continue;
    }
    const a = document.createElement('a');
    a.href = '#';
    a.textContent = item.label;
    links.append(a);
  }
  nav.append(links);
  for (const item of items) {
    if (item.cta !== true) {
      continue;
    }
    const a = document.createElement('a');
    a.className = 'btn btn--primary';
    a.href = '#';
    a.textContent = item.label;
    nav.append(a);
  }
  inner.append(nav);
  header.append(inner);
  return header;
}

function renderHero(hero: NonNullable<HomeDraft['hero']>): HTMLElement {
  const section = document.createElement('section');
  section.className = 'hero';
  const container = document.createElement('div');
  container.className = 'container';

  if (typeof hero.eyebrow === 'string') {
    container.append(eyebrow(hero.eyebrow, true));
  }
  if (typeof hero.headline === 'string') {
    const h1 = document.createElement('h1');
    h1.className = 'hero__headline';
    h1.textContent = hero.headline;
    container.append(h1);
  }
  if (typeof hero.subhead === 'string') {
    const p = document.createElement('p');
    p.className = 'hero__subhead';
    p.textContent = hero.subhead;
    container.append(p);
  }
  if (typeof hero.posture === 'string') {
    const p = document.createElement('p');
    p.className = 'hero__posture';
    p.textContent = hero.posture;
    container.append(p);
  }

  const row = document.createElement('div');
  row.className = 'btn-row';
  if (
    hero.primaryCta !== undefined &&
    typeof hero.primaryCta.label === 'string'
  ) {
    row.append(cta(hero.primaryCta.label, 'primary'));
  }
  if (
    hero.secondaryCta !== undefined &&
    typeof hero.secondaryCta.label === 'string'
  ) {
    row.append(cta(hero.secondaryCta.label, 'secondary'));
  }
  if (row.childElementCount > 0) {
    container.append(row);
  }
  section.append(container);
  return section;
}

function renderApps(apps: NonNullable<HomeDraft['apps']>): HTMLElement {
  const section = document.createElement('section');
  section.className = 'section';
  const container = document.createElement('div');
  container.className = 'container';
  container.append(sectionHead(apps.eyebrow, apps.headline));

  const grid = document.createElement('div');
  grid.className = 'card-grid card-grid--3';
  for (const item of apps.items ?? []) {
    const card = document.createElement('article');
    card.className = 'card';
    if (typeof item.name === 'string') {
      const h3 = document.createElement('h3');
      h3.textContent = item.name;
      card.append(h3);
    }
    if (typeof item.body === 'string') {
      const p = document.createElement('p');
      p.textContent = item.body;
      card.append(p);
    }
    grid.append(card);
  }
  container.append(grid);
  section.append(container);
  return section;
}

function renderSimpleSection(block: {
  eyebrow?: string;
  headline?: string;
  body?: string;
}): HTMLElement {
  const section = document.createElement('section');
  section.className = 'section';
  const container = document.createElement('div');
  container.className = 'container';
  container.append(sectionHead(block.eyebrow, block.headline));
  if (typeof block.body === 'string') {
    const p = document.createElement('p');
    p.className = 'lede';
    p.textContent = block.body;
    container.append(p);
  }
  section.append(container);
  return section;
}

function renderHowItWorks(
  block: NonNullable<HomeDraft['howItWorks']>,
): HTMLElement {
  const section = document.createElement('section');
  section.className = 'section section--ink';
  const container = document.createElement('div');
  container.className = 'container';
  container.append(sectionHead(block.eyebrow, block.headline));
  if (typeof block.body === 'string') {
    const p = document.createElement('p');
    p.className = 'lede';
    p.textContent = block.body;
    container.append(p);
  }
  const steps = document.createElement('ol');
  steps.className = 'steps';
  (block.steps ?? []).forEach((step, index) => {
    const li = document.createElement('li');
    li.className = 'steps__item';
    const n = document.createElement('span');
    n.className = 'steps__n';
    n.setAttribute('aria-hidden', 'true');
    n.textContent = String(index + 1);
    const body = document.createElement('div');
    if (typeof step.title === 'string') {
      const h3 = document.createElement('h3');
      h3.textContent = step.title;
      body.append(h3);
    }
    if (typeof step.body === 'string') {
      const p = document.createElement('p');
      p.textContent = step.body;
      body.append(p);
    }
    li.append(n, body);
    steps.append(li);
  });
  container.append(steps);
  section.append(container);
  return section;
}

function renderCredibility(
  block: NonNullable<HomeDraft['credibility']>,
): HTMLElement {
  const section = document.createElement('section');
  section.className = 'section';
  const container = document.createElement('div');
  container.className = 'container';
  container.append(sectionHead(block.eyebrow, block.headline, true));
  const panel = document.createElement('div');
  panel.className = 'panel';
  if (typeof block.body === 'string') {
    const p = document.createElement('p');
    p.textContent = block.body;
    panel.append(p);
  }
  const partner = document.createElement('p');
  partner.className = 'panel__partner';
  const strong = document.createElement('strong');
  strong.textContent = 'Cross Healthcare Services';
  const note = document.createElement('span');
  note.className = 'panel__note';
  note.textContent = 'Founding operator partner';
  partner.append(strong, note);
  panel.append(partner);
  container.append(panel);
  section.append(container);
  return section;
}

function renderAct(block: NonNullable<HomeDraft['act']>): HTMLElement {
  const section = document.createElement('section');
  section.className = 'section';
  const container = document.createElement('div');
  container.className = 'container';
  container.append(sectionHead(block.eyebrow, block.headline));
  if (typeof block.body === 'string') {
    const p = document.createElement('p');
    p.className = 'lede';
    p.textContent = block.body;
    container.append(p);
  }
  if (typeof block.trust === 'string') {
    const callout = document.createElement('p');
    callout.className = 'callout';
    callout.textContent = block.trust;
    container.append(callout);
  }
  if (
    block.primaryCta !== undefined &&
    typeof block.primaryCta.label === 'string'
  ) {
    const row = document.createElement('div');
    row.className = 'btn-row';
    row.append(cta(block.primaryCta.label, 'primary'));
    container.append(row);
  }
  section.append(container);
  return section;
}

function renderFooter(site: SiteDraft | null): HTMLElement {
  const footer = document.createElement('footer');
  footer.className = 'site-footer';
  const container = document.createElement('div');
  container.className = 'container';

  const top = document.createElement('div');
  top.className = 'site-footer__top';
  const tagline = document.createElement('p');
  tagline.textContent =
    site?.footer?.tagline ?? 'Connected care. Better outcomes.';
  top.append(tagline);

  const legal = document.createElement('nav');
  legal.className = 'site-footer__legal';
  legal.setAttribute('aria-label', 'Legal');
  for (const item of site?.footer?.legal ?? []) {
    const a = document.createElement('a');
    a.href = '#';
    a.textContent = item.label;
    legal.append(a);
  }
  top.append(legal);
  container.append(top);

  const status = document.createElement('div');
  status.className = 'site-footer__status';
  const pill = document.createElement('span');
  pill.className = 'status-pill';
  pill.textContent = site?.footer?.statusLine ?? 'built with operators';
  const honesty = document.createElement('span');
  honesty.textContent =
    site?.footer?.honesty ??
    'Built by people who run skilled-nursing buildings.';
  status.append(pill, honesty);
  container.append(status);
  footer.append(container);
  return footer;
}

function sectionHead(
  eyebrowText: string | undefined,
  headline: string | undefined,
  dot = false,
): HTMLElement {
  const head = document.createElement('div');
  head.className = 'section-head';
  if (typeof eyebrowText === 'string') {
    head.append(eyebrow(eyebrowText, dot));
  }
  if (typeof headline === 'string') {
    const h2 = document.createElement('h2');
    h2.textContent = headline;
    head.append(h2);
  }
  return head;
}

function eyebrow(text: string, dot: boolean): HTMLElement {
  const el = document.createElement('p');
  el.className = dot ? 'eyebrow eyebrow--dot' : 'eyebrow';
  el.textContent = text;
  return el;
}

function cta(label: string, variant: 'primary' | 'secondary'): HTMLElement {
  const a = document.createElement('a');
  a.className = `btn btn--${variant}`;
  a.href = '#';
  a.textContent = label;
  return a;
}
